import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { RevealLines, Reveal } from "./Reveal";

const easing = [0.22, 1, 0.36, 1];

/** Reusable interior page hero band (navy, clears fixed nav). */
export const PageHeader = ({ overline, titleLines = [], accentLines = [], subtitle }) => (
  <section className="grain relative overflow-hidden bg-navy pt-40 pb-16 sm:pt-48 sm:pb-24" data-testid="page-header">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[150px]" />
    </div>
    <div className="container-luxe relative z-10">
      {overline && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="overline mb-7 flex items-center gap-4"
        >
          <span className="h-px w-10 bg-gold" /> {overline}
        </motion.p>
      )}
      <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl">
        <RevealLines lines={titleLines} delay={0.2} />
        {accentLines.length > 0 && (
          <RevealLines lines={accentLines} lineClassName="italic text-gold" delay={0.2 + titleLines.length * 0.12} />
        )}
      </h1>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-8 max-w-2xl text-base font-light leading-relaxed text-cream/70 sm:text-lg"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  </section>
);

/** Reusable section heading used inside pages. */
export const SectionHeading = ({ overline, title, accent, subtitle, dark = true, className = "" }) => (
  <div className={`mb-14 ${className}`}>
    <Reveal>
      <p className={`overline mb-6 flex items-center gap-4 ${dark ? "" : "text-gold-deep"}`}>
        <span className={`h-px w-10 ${dark ? "bg-gold" : "bg-gold-deep"}`} /> {overline}
      </p>
    </Reveal>
    <Reveal delay={0.1}>
      <h2
        className={`max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl ${
          dark ? "text-cream" : "text-navy"
        }`}
      >
        {title} {accent && <span className={`italic ${dark ? "text-gold" : "text-gold-deep"}`}>{accent}</span>}
      </h2>
    </Reveal>
    {subtitle && (
      <Reveal delay={0.2}>
        <p className={`mt-6 max-w-2xl text-base font-light leading-relaxed ${dark ? "text-cream/70" : "text-navy/70"}`}>
          {subtitle}
        </p>
      </Reveal>
    )}
  </div>
);

/** Reusable closing CTA band. */
export const CTASection = () => (
  <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="cta-section">
    <div className="container-luxe relative z-10 flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between">
      <Reveal>
        <h2 className="max-w-2xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
          Da el primer paso <span className="italic text-gold">hacia una vida consciente</span>.
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <Link to="/contact" className="btn-gold shrink-0" data-testid="cta-button">
          Comenzar ahora
        </Link>
      </Reveal>
    </div>
  </section>
);

export const easings = easing;
